import React from 'react';
import { Box, Typography } from '@mui/material';
import { MusicNote, History, Search, QueueMusic } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SONOS_BLUE, SURFACE_BORDER } from './sonosUiStyles';
import SonosGroupSpeakerIcon from './SonosGroupSpeakerIcon';

export type PlaybackSubTab = 'playlists' | 'recent' | 'search' | 'queue';

interface PlaybackSubTabsProps {
  value: PlaybackSubTab;
  onChange: (tab: PlaybackSubTab) => void;
  hasSpeaker?: boolean;
  destCount?: number;
  onOpenCompactPlayer?: () => void;
}

const TABS: { id: PlaybackSubTab; icon: React.ReactElement }[] = [
  { id: 'playlists', icon: <MusicNote sx={{ fontSize: 13 }} /> },
  { id: 'queue', icon: <QueueMusic sx={{ fontSize: 13 }} /> },
  { id: 'recent', icon: <History sx={{ fontSize: 13 }} /> },
  { id: 'search', icon: <Search sx={{ fontSize: 13 }} /> },
];

const PlaybackSubTabs: React.FC<PlaybackSubTabsProps> = ({
  value,
  onChange,
  hasSpeaker = false,
  destCount = 0,
  onOpenCompactPlayer,
}) => {
  const { t } = useTranslation();
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'stretch',
      borderBottom: `1px solid ${SURFACE_BORDER}`,
      backgroundColor: '#000',
      flexShrink: 0,
      width: '100%',
      minWidth: 0,
      minHeight: 44,
      pt: 0.35,
      mb: 0.5,
      boxSizing: 'border-box',
    }}>
      <Box sx={{
        display: 'flex',
        flex: 1,
        minWidth: 0,
      }}>
        {TABS.map(({ id, icon }) => {
          const active = value === id;
          return (
            <Box
              key={id}
              onClick={() => onChange(id)}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.2,
                py: 0.55,
                px: 0.15,
                cursor: 'pointer',
                borderBottom: active ? `2px solid ${SONOS_BLUE}` : '2px solid transparent',
                color: active ? SONOS_BLUE : '#fff',
                minWidth: 0,
                userSelect: 'none',
              }}
            >
              {icon}
              <Typography sx={{
                fontSize: 11.5,
                fontWeight: active ? 700 : 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.1,
              }}>
                {t(`musicSystem.playback.${id}`)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {onOpenCompactPlayer ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            pl: 0.15,
            pr: 0.25,
            pb: 0.1,
            borderBottom: '2px solid transparent',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <SonosGroupSpeakerIcon
            hasSpeaker={hasSpeaker}
            destCount={destCount}
            onClick={(e) => {
              e.stopPropagation();
              onOpenCompactPlayer();
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default PlaybackSubTabs;
